import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { isNaN } from 'lodash';
import 'url-search-params-polyfill';
import {
  getChartData,
  getSiteData,
  getAsset,
  getCurrentStatus,
  changeDateRange,
  toggleMode
} from "../actions";
import { getConfig } from "../reducers/config";

import OverviewChart from "../components/charts/OverviewChart";
import ValueColorWrapper from "../components/common/ValueColorWrapper";
import getPlcStatusWord1 from '../selectors/get_plc_status_word_1_alarm';

// Material UI
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import green from "@material-ui/core/colors/green";
import grey from "@material-ui/core/colors/grey";
import red from "@material-ui/core/colors/red";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const styles = () => ({
  negativeMargin: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '20px'
  },
  root: {
    flexGrow: 1
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
  },
  card: {
    flexGrow: 1
  },
  outline: {
    fill: grey[700]
  },
  power: {
    top: 40,
    fill: green[500]
  },
  table: {},
  soh: {
    display: 'block',
  },
  alarmError: {
    color: red[500]
  }
});

function Dashboard(props) {
  const {
    classes,
    config: {
      config
    },
    isAtypical,
    currentLoad,
    setpoint,
    genericPmax,
    pMaxOffPeak,
    pMaxOnPeak,
    pMaxLoad,
    pMaxLoadWithoutBess,
    bessInstantPower,
    bessStatus,
    soc,
    soh,
    genericPRef,
    realtime,
    startDate,
    endDate,
    plcStatusWord1Alarm,
  } = props;

  const query = useQuery();

  const populateState = () => {
    props.getCurrentStatus();
    props.getSiteData();
    props.getAsset();
  };

  const getDeviation = (value1, value2) => ({
    percentage: Math.round((1 - (value1 / value2)) * 100),
    power: Math.round(value2 - value1)
  });

  const setRealtimeDateRange = () => {
    const start = moment().subtract(24, "h").startOf('hour');
    const end = moment();
    props.changeDateRange({ startDate: start, endDate: end }, true);
  };

  const setRealtimeInterval = () => {
    setRealtimeDateRange();
    return setInterval(
      () => {
        props.getSiteData();
        props.getCurrentStatus();
        setRealtimeDateRange();
      },
      30000
    );
  };

  useEffect(() => {
    const startDateQuery = parseInt(query.get('startDate'), 10);
    const endDateQuery = parseInt(query.get('endDate'), 10);

    if (!startDateQuery && !endDateQuery && !realtime) {
      props.toggleMode();
    } else if (startDateQuery && endDateQuery) {
      props.changeDateRange(
        {
          startDate: moment(startDateQuery),
          endDate: moment(endDateQuery)
        },
        false
      );
    }

    populateState();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      props.getChartData();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    let realtimeIntervalId;

    if (realtime) {
      realtimeIntervalId = setRealtimeInterval();
    }

    return () => clearInterval(realtimeIntervalId);
  }, [realtime]);

  const handleBESSStatus = batteryState => {
    switch (batteryState) {
      case 0:
        return "Disabled";
      case 1:
        return "Offline";
      case 2:
        return "Stand by";
      case 3:
        return "Charging";
      case 4:
        return "Discharging";
      case 5:
        return "Islanding Mode";
      case null:
      case undefined:
        return "--";
      default:
        return "Available";
    }
  };

  const deviation = isAtypical ?
    getDeviation(pMaxOnPeak, pMaxOffPeak) :
    getDeviation(pMaxLoad, pMaxLoadWithoutBess);

  return (
    <React.Fragment>
      <div className={classes.negativeMargin}>
        <Grid container spacing={3} className={classes.root}>
          <Grid item xs={3} className={classes.column}>
            <Card className={classes.card}>
              <CardContent className={classes.content}>
                <Typography
                  component="h5"
                  variant="h5"
                  style={{ marginBottom: 15, textAlign: "center" }}
                >
                  {config.SITE_NAME}
                </Typography>

                <Table className={classes.table} padding="default">
                  <TableBody>
                    <TableRow>
                      <TableCell>Current Load</TableCell>
                      <TableCell>
                        <ValueColorWrapper>
                          {Math.round(currentLoad) || '--'}
                        </ValueColorWrapper>{" "}
                        kW
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Setpoint</TableCell>
                      <TableCell>
                        <ValueColorWrapper>
                          {Math.round(setpoint)}
                        </ValueColorWrapper>&nbsp;kW
                      </TableCell>
                    </TableRow>
                    {!isAtypical && (
                      <React.Fragment>
                        <TableRow>
                          <TableCell>P Ref</TableCell>
                          <TableCell>{genericPRef} kW</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>P Max</TableCell>
                          <TableCell>{(genericPmax && Math.round(genericPmax)) || '--'} kW</TableCell>
                        </TableRow>
                      </React.Fragment>
                    )}
                    {(isAtypical && (
                      <React.Fragment>
                        <TableRow>
                          <TableCell>Pmax off peak</TableCell>
                          <TableCell>
                            {Math.round(pMaxOffPeak) || '--'}&nbsp;kW
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Pmax on peak</TableCell>
                          <TableCell>
                            {Math.round(pMaxOnPeak) || '--'}&nbsp;kW
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    )) || null}

                    <TableRow>
                      <TableCell>Current Deviation</TableCell>
                      <TableCell>
                        {isNaN(deviation.percentage) ? '--' : deviation.percentage}&nbsp;%
                        <br/>
                        {isNaN(deviation.percentage) ? '--' : deviation.power}&nbsp;kW
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>BESS Instant Power</TableCell>
                      <TableCell>
                        <ValueColorWrapper>
                          {Math.round(bessInstantPower)}
                        </ValueColorWrapper>&nbsp;kW
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>BESS Status</TableCell>
                      <TableCell>
                        {handleBESSStatus(bessStatus)}
                      </TableCell>
                    </TableRow>
                    {
                      plcStatusWord1Alarm ? (
                        <TableRow>
                          <TableCell
                            className={plcStatusWord1Alarm ? classes.alarmError : ''}
                          >Alarm</TableCell>
                          <TableCell
                            className={plcStatusWord1Alarm ? classes.alarmError : ''}
                          >
                            {plcStatusWord1Alarm ? 'ON' : 'Off'}
                          </TableCell>
                        </TableRow>
                      ) : null
                    }
                  </TableBody>
                </Table>
                <div style={{ maxWidth: "70%", margin: "0 auto" }}>
                  <div
                    style={{ position: "relative", margin: "16px 0 0 " }}
                  >
                      <span
                        style={{
                          position: "absolute",
                          left: "43%",
                          top: "43%"
                        }}
                      >
                        {soc || soc === 0
                          ? `${Math.round(soc)} %`
                          : '--'}
                      </span>
                    <svg
                      id="Layer_1"
                      data-name="Layer 1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 257.13 142.85"
                    >
                      <defs/>
                      <title>battery</title>

                      <path
                        className={classes.outline}
                        d="M257.13,92.85a14.24,14.24,0,0,1-14.29,14.29V125A17.91,17.91,0,0,1,225,142.85H17.86A17.91,17.91,0,0,1,0,125V17.86A17.91,17.91,0,0,1,17.86,0H225a17.91,17.91,0,0,1,17.86,17.86V35.71A14.24,14.24,0,0,1,257.13,50Zm-14.29,0V50H228.56V17.86A3.53,3.53,0,0,0,225,14.29H17.86a3.52,3.52,0,0,0-3.57,3.57V125a3.52,3.52,0,0,0,3.57,3.57H225a3.53,3.53,0,0,0,3.57-3.57V92.85Z"
                      />

                      <rect
                        className={classes.power}
                        x="23"
                        y="23"
                        width={soc * 2}
                        height="96"
                      />
                    </svg>
                  </div>

                  {
                    (soh !== null &&
                      <Typography
                        component="p"
                        style={{ marginTop: 5, marginBottom: 0, textAlign: "center", display: 'block' }}
                      >
                        Battery health : {Math.round(soh)}%
                      </Typography>) || null
                  }
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={9} className={classes.column}>
            <OverviewChart/>
          </Grid>
        </Grid>
      </div>
    </React.Fragment>
  );
}

Dashboard.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  currentLoad: state.siteData.currentLoad,
  setpoint: state.siteData.setpoint,
  genericPmax: state.siteData.genericPmax,
  pMaxOffPeak: state.siteData.pMaxOffPeak,
  pMaxOnPeak: state.siteData.pMaxOnPeak,
  pMaxLoad: state.siteData.pMaxLoad,
  pMaxLoadWithoutBess: state.siteData.pMaxLoadWithoutBess,
  bessInstantPower: state.siteData.bessInstantPower,
  bessStatus: state.siteData.bessStatus,
  soc: state.siteData.soc,
  soh: state.siteData.soh,
  genericPRef: state.asset.peak_shaving_generic.p_ref_kw,
  realtime: state.mode.realtime,
  siteData: state.siteData,
  config: getConfig(state),
  isAtypical: state.config.config.ATYPICAL,
  startDate: state.dateRange.startDate,
  endDate: state.dateRange.endDate,
  plcStatusWord1Alarm: getPlcStatusWord1(state)
});

export default withStyles(styles)(
  connect(
    mapStateToProps,
    {
      getAsset,
      getChartData,
      getSiteData,
      changeDateRange,
      getCurrentStatus,
      toggleMode
    }
  )(Dashboard)
);



// WEBPACK FOOTER //
// ./src/pages/Dashboard.js